import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { AdminNavigation } from '@/components/AdminNavigation';
import { formatPrice } from '@/lib/utils';
import { 
  Eye, EyeOff, Grid, List, Search, SortAsc, SortDesc, 
  DollarSign, TrendingUp, Package, Calendar, Filter,
  Edit2, Trash2, Save, X, Upload, Plus, Download, Settings, Clock,
  Utensils, Flame, ChefHat, ArrowUp, ArrowDown, GripVertical, ShoppingCart, Percent, Leaf, Snowflake
} from 'lucide-react';
import type { RestaurantProduct, InsertRestaurantProduct, UpdateRestaurantProduct, RESTAURANT_CATEGORIES } from '@shared/schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Link } from 'wouter';
import { RestaurantCSVImportExport } from '@/components/restaurant-csv-import-export';
import { RestaurantCategoriesManager } from '@/components/RestaurantCategoriesManager';
import { ListOrdered } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type SortField = 'name' | 'pricePerUnit' | 'category' | 'manual';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'grid' | 'list';

// Restaurant category configuration
const RESTAURANT_CATEGORY_CONFIG = {
  'Rice & Noodles': { label: 'Rice & Noodles', color: 'bg-amber-100 text-amber-800', icon: Package },
  'Proteins': { label: 'Proteins', color: 'bg-red-100 text-red-800', icon: Utensils },
  'Vegetables': { label: 'Vegetables', color: 'bg-green-100 text-green-800', icon: Leaf },
  'Sauces & Condiments': { label: 'Sauces & Condiments', color: 'bg-orange-100 text-orange-800', icon: Package },
  'Dim Sum & Frozen': { label: 'Dim Sum & Frozen', color: 'bg-blue-100 text-blue-800', icon: Snowflake },
  'Cooking Essentials': { label: 'Cooking Essentials', color: 'bg-purple-100 text-purple-800', icon: Flame }
};

// Safely parse discount tags JSON
function parseDiscountTags(tagsJson: string | null | undefined): string[] {
  try {
    const parsed = JSON.parse(tagsJson || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Product card component
function RestaurantProductCard({ product, onEdit, onDelete, onToggleActive }: { 
  product: RestaurantProduct; 
  onEdit: (product: RestaurantProduct) => void;
  onDelete: (id: number) => void;
  onToggleActive: (id: number, active: boolean) => void;
}) {
  const { isAdmin } = useAuth();
  const [imageError, setImageError] = useState(false);
  const categoryConfig = RESTAURANT_CATEGORY_CONFIG[product.category as keyof typeof RESTAURANT_CATEGORY_CONFIG];
  const Icon = categoryConfig?.icon || Package;
  
  return (
    <motion.div
      key={product.id}
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
      className="h-full"
    >
      <Card className={`h-full hover:shadow-lg transition-shadow border-[1.5px] border-gray-400 ${!product.active ? 'opacity-50' : ''}`}>
        <CardContent className="p-3">
          {/* Category Badge */}
          <div className="flex justify-between items-start mb-2">
            <Badge className={`${categoryConfig?.color || 'bg-gray-100 text-gray-800'} text-xs`}>
              <Icon className="w-3 h-3 mr-1" />
              {categoryConfig?.label || product.category}
            </Badge>
            <Switch 
              checked={product.active} 
              onCheckedChange={isAdmin() ? (checked) => onToggleActive(product.id, checked) : undefined}
              disabled={!isAdmin()}
              className="scale-90"
            />
          </div>

          {/* Product Image */}
          <div className="w-full h-24 bg-gray-100 rounded-lg mb-2 overflow-hidden">
            {product.imageUrl && !imageError ? (
              <img 
                src={product.imageUrl} 
                alt={product.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Utensils className="w-10 h-10 text-gray-400" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <h3 className="font-semibold text-base mb-0.5">{product.name}</h3>
          <p className="text-gray-600 text-xs mb-2 line-clamp-1">{product.description}</p>

          {/* Pricing and Unit Info */}
          <div className="space-y-1 mb-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-xs">Price:</span>
              <span className="font-bold text-emerald-600 text-lg">
                {formatPrice(product.pricePerUnit)}/{product.unit}
              </span>
            </div>
            {product.size && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-xs">Size:</span>
                <span className="font-medium text-sm">{product.size}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-xs">Min Order:</span>
              <span className="font-medium text-sm">
                {product.minOrderQuantity} {product.unit}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {isAdmin() ? (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => onEdit(product)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700"
                  onClick={() => onDelete(product.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <div className="flex-1 text-center text-xs text-gray-400 py-2">
                View Only
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Add/Edit Product Modal
function RestaurantProductModal({ 
  product, 
  isOpen, 
  onClose, 
  onSave 
}: { 
  product?: RestaurantProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: InsertRestaurantProduct | UpdateRestaurantProduct) => void;
}) {
  const [categories, setCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'Rice & Noodles',
    unit: product?.unit || 'box',
    size: product?.size || '',
    minOrderQuantity: product?.minOrderQuantity || 1,
    pricePerUnit: product?.pricePerUnit || 0,
    imageUrl: product?.imageUrl || '',
    active: product?.active ?? true,
    displayOrder: product?.displayOrder || 0,
    discountTags: parseDiscountTags(product?.discountTags)
  });
  
  const { toast } = useToast();

  // Fetch discount tier configurations
  const { data: discountMatrix } = useQuery({
    queryKey: ['admin-restaurant-discount-matrix'],
    queryFn: () => apiRequest<{
      tiers: Array<{ tier: string; percentage: number; label: string }>;
      codes: any[];
    }>('/api/admin/restaurant/discount-matrix'),
    enabled: isOpen
  });

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetch('/api/restaurant/categories')
        .then(res => res.json())
        .then(data => {
          setCategories(data);
          // If no category is set and we have categories, use the first one
          if (!product && data.length > 0 && !formData.category) {
            setFormData(prev => ({ ...prev, category: data[0].name }));
          }
        })
        .catch(err => console.error('Failed to fetch categories:', err));
    }
  }, [isOpen]);

  useEffect(() => {
    if (product) {
      try {
        setFormData({
          name: product.name,
          description: product.description,
          category: product.category,
          unit: product.unit,
          size: product.size || '',
          minOrderQuantity: product.minOrderQuantity,
          pricePerUnit: product.pricePerUnit,
          imageUrl: product.imageUrl || '',
          active: product.active,
          displayOrder: product.displayOrder,
          discountTags: parseDiscountTags(product.discountTags)
        });
      } catch (e) {
        console.error("Error parsing discount tags:", e);
        setFormData({
          name: product.name,
          description: product.description,
          category: product.category,
          unit: product.unit,
          size: product.size || '',
          minOrderQuantity: product.minOrderQuantity,
          pricePerUnit: product.pricePerUnit,
          imageUrl: product.imageUrl || '',
          active: product.active,
          displayOrder: product.displayOrder,
          discountTags: []
        });
      }
    } else {
      setFormData({
        name: '',
        description: '',
        category: 'Rice & Noodles',
        unit: 'box',
        size: '',
        minOrderQuantity: 1,
        pricePerUnit: 0,
        imageUrl: '',
        active: true,
        displayOrder: 0,
        discountTags: []
      });
    }
  }, [product?.id, isOpen]);

  // Handle image file upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select a valid image file.',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 5MB.',
          variant: 'destructive'
        });
        return;
      }
      
      // Upload file to server
      const formData = new FormData();
      formData.append('image', file);
      
      // Get CSRF token and session ID from localStorage
      const csrfToken = localStorage.getItem('csrfToken');
      const sessionId = localStorage.getItem('sessionId');
      
      const headers: HeadersInit = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }
      
      try {
        const response = await fetch('/api/admin/upload/product-image', {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include'
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Upload failed');
        }
        
        const result = await response.json();
        setFormData(prev => ({ ...prev, imageUrl: result.imageUrl }));
        
        toast({
          title: 'Image uploaded',
          description: 'Image uploaded successfully',
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: 'Upload failed',
          description: error.message || 'Failed to upload image',
          variant: 'destructive'
        });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation - only check for name
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Product name is required',
        variant: 'destructive'
      });
      return;
    }
    
    const dataToSave = {
      ...formData,
      // Ensure all required fields have defaults
      description: formData.description || '',
      category: formData.category || 'Rice & Noodles',
      unit: formData.unit || 'box',
      minOrderQuantity: formData.minOrderQuantity || 1,
      pricePerUnit: formData.pricePerUnit || 0,
      displayOrder: formData.displayOrder || 0,
      discountTags: JSON.stringify(formData.discountTags || [])
    };
    onSave(dataToSave);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? 'Edit Restaurant Product' : 'Add Restaurant Product'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter product name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => {
                    const config = RESTAURANT_CATEGORY_CONFIG[category.name as keyof typeof RESTAURANT_CATEGORY_CONFIG];
                    const Icon = config?.icon || Package;
                    return (
                      <SelectItem key={category.id} value={category.name}>
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded ${config?.color || 'bg-gray-100 text-gray-800'}`}>
                            <Icon className="h-3 w-3" />
                          </div>
                          <span>{category.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Enter product description (optional)"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pricePerUnit">Price per Unit ($)</Label>
              <Input
                id="pricePerUnit"
                type="number"
                step="0.01"
                value={formData.pricePerUnit}
                onChange={(e) => setFormData({ ...formData, pricePerUnit: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit</Label>
              <Input
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="Enter unit (e.g., box, case, kg, lb, etc.)"
                list="unit-suggestions"
              />
              <datalist id="unit-suggestions">
                <option value="box">Box</option>
                <option value="case">Case</option>
                <option value="bag">Bag</option>
                <option value="pack">Pack</option>
                <option value="bunch">Bunch</option>
                <option value="each">Each</option>
                <option value="lb">Pound (lb)</option>
                <option value="kg">Kilogram (kg)</option>
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="size">Size (Optional)</Label>
              <Input
                id="size"
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                placeholder="e.g., 30lb, 10kg, 24x600g"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minOrderQuantity">Minimum Order Quantity</Label>
              <Input
                id="minOrderQuantity"
                type="number"
                min="0"
                value={formData.minOrderQuantity}
                onChange={(e) => setFormData({ ...formData, minOrderQuantity: parseInt(e.target.value) || 1 })}
                placeholder="1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayOrder">Display Order</Label>
              <Input
                id="displayOrder"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Product Image</Label>
            <div className="space-y-3">
              {formData.imageUrl ? (
                <div className="space-y-2">
                  <div className="relative inline-block">
                    <img 
                      src={formData.imageUrl} 
                      alt="Product preview" 
                      className="w-32 h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.imageUrl.startsWith('data:') ? 'Uploaded image' : formData.imageUrl}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">Choose one option:</div>
                  
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="font-medium text-sm">Option 1: Upload Image</div>
                    <label className="w-full h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors">
                      <Upload className="h-6 w-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Click to upload (max 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="font-medium text-sm">Option 2: Image URL</div>
                    <Input
                      type="text"
                      value=""
                      onChange={(e) => {
                        const url = e.target.value.trim();
                        if (url) {
                          setFormData({ ...formData, imageUrl: url });
                        }
                      }}
                      placeholder="/images/products/filename.jpg"
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      Use existing images from /images/products/
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <Separator className="my-4" />

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Discount Tiers
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="tier0"
                  checked={formData.discountTags?.includes('tier0') || false}
                  onChange={(e) => {
                    const tags = formData.discountTags || [];
                    if (e.target.checked) {
                      setFormData({ ...formData, discountTags: [...tags, 'tier0'] });
                    } else {
                      setFormData({ ...formData, discountTags: tags.filter(t => t !== 'tier0') });
                    }
                  }}
                  className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <Label htmlFor="tier0" className="flex items-center gap-1 cursor-pointer">
                  {discountMatrix?.tiers?.find(t => t.tier === 'tier0')?.label || 'Regular'} ({(discountMatrix?.tiers?.find(t => t.tier === 'tier0')?.percentage || 0) * 100}% off)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="tier1"
                  checked={formData.discountTags?.includes('tier1') || false}
                  onChange={(e) => {
                    const tags = formData.discountTags || [];
                    if (e.target.checked) {
                      setFormData({ ...formData, discountTags: [...tags, 'tier1'] });
                    } else {
                      setFormData({ ...formData, discountTags: tags.filter(t => t !== 'tier1') });
                    }
                  }}
                  className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <Label htmlFor="tier1" className="flex items-center gap-1 cursor-pointer">
                  {discountMatrix?.tiers?.find(t => t.tier === 'tier1')?.label || 'Tier 1'} ({(discountMatrix?.tiers?.find(t => t.tier === 'tier1')?.percentage || 0.07) * 100}% off)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="tier2"
                  checked={formData.discountTags?.includes('tier2') || false}
                  onChange={(e) => {
                    const tags = formData.discountTags || [];
                    if (e.target.checked) {
                      setFormData({ ...formData, discountTags: [...tags, 'tier2'] });
                    } else {
                      setFormData({ ...formData, discountTags: tags.filter(t => t !== 'tier2') });
                    }
                  }}
                  className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <Label htmlFor="tier2" className="flex items-center gap-1 cursor-pointer">
                  {discountMatrix?.tiers?.find(t => t.tier === 'tier2')?.label || 'Tier 2'} ({(discountMatrix?.tiers?.find(t => t.tier === 'tier2')?.percentage || 0.10) * 100}% off)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="tier3"
                  checked={formData.discountTags?.includes('tier3') || false}
                  onChange={(e) => {
                    const tags = formData.discountTags || [];
                    if (e.target.checked) {
                      setFormData({ ...formData, discountTags: [...tags, 'tier3'] });
                    } else {
                      setFormData({ ...formData, discountTags: tags.filter(t => t !== 'tier3') });
                    }
                  }}
                  className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <Label htmlFor="tier3" className="flex items-center gap-1 cursor-pointer">
                  {discountMatrix?.tiers?.find(t => t.tier === 'tier3')?.label || 'Tier 3'} ({(discountMatrix?.tiers?.find(t => t.tier === 'tier3')?.percentage || 0.15) * 100}% off)
                </Label>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Select which discount tiers apply to this product. Products will be discounted for customers with matching tier codes.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {product ? 'Update' : 'Add'} Product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRestaurantProducts() {
  const { isAdmin } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('manual');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showInactive, setShowInactive] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<RestaurantProduct | null>(null);
  const { toast } = useToast();

  // Set page title
  useEffect(() => {
    document.title = 'Restaurant Products - Tops100';
  }, []);

  // Fetch restaurant products
  const { data: products, isLoading } = useQuery({
    queryKey: ['admin-restaurant-products'],
    queryFn: () => apiRequest<RestaurantProduct[]>('/api/admin/restaurant/products')
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['restaurant-categories'],
    queryFn: () => apiRequest('/api/restaurant/categories')
  });

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: (data: InsertRestaurantProduct) => 
      apiRequest('POST', '/api/admin/restaurant/products', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-products'] });
      toast({ title: 'Product created successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to create product', variant: 'destructive' });
    }
  });

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRestaurantProduct }) => 
      apiRequest('PUT', `/api/admin/restaurant/products/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-products'] });
      toast({ title: 'Product updated successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to update product', variant: 'destructive' });
    }
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest('DELETE', `/api/admin/restaurant/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-products'] });
      toast({ title: 'Product deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to delete product', variant: 'destructive' });
    }
  });

  // Toggle active status
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => 
      apiRequest('PUT', `/api/admin/restaurant/products/${id}`, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-products'] });
    }
  });

  // Update display order
  const updateOrderMutation = useMutation({
    mutationFn: ({ id, displayOrder }: { id: number; displayOrder: number }) => 
      apiRequest('PATCH', `/api/admin/restaurant/products/${id}/order`, { displayOrder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-products'] });
    }
  });

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    let filtered = products.filter(product => {
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesActive = showInactive || product.active;
      
      return matchesCategory && matchesActive;
    });

    // Sort products
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'pricePerUnit':
          comparison = a.pricePerUnit - b.pricePerUnit;
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'manual':
          comparison = a.displayOrder - b.displayOrder;
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [products, selectedCategory, showInactive, sortField, sortOrder]);

  // Stats calculation
  const stats = useMemo(() => {
    if (!products) return { total: 0, active: 0, categories: {}, averagePrice: 0 };
    
    const categoryCount: Record<string, number> = {};
    let activeCount = 0;
    let totalPrice = 0;
    let priceCount = 0;
    
    products.forEach(product => {
      categoryCount[product.category] = (categoryCount[product.category] || 0) + 1;
      if (product.active) activeCount++;
      if (product.pricePerUnit > 0) {
        totalPrice += product.pricePerUnit;
        priceCount++;
      }
    });
    
    return {
      total: products.length,
      active: activeCount,
      categories: categoryCount,
      averagePrice: priceCount > 0 ? totalPrice / priceCount : 0
    };
  }, [products]);

  const handleEdit = (product: RestaurantProduct) => {
    setEditingProduct(product);
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this product?')) {
      deleteProductMutation.mutate(id);
    }
  };

  const handleToggleActive = (id: number, active: boolean) => {
    toggleActiveMutation.mutate({ id, active });
  };

  const handleSaveProduct = (data: InsertRestaurantProduct | UpdateRestaurantProduct) => {
    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, data });
    } else {
      createProductMutation.mutate(data as InsertRestaurantProduct);
    }
  };

  const handleMoveProduct = (product: RestaurantProduct, direction: 'up' | 'down') => {
    const currentIndex = filteredProducts.findIndex(p => p.id === product.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < filteredProducts.length) {
      const otherProduct = filteredProducts[newIndex];
      
      // Swap display orders
      updateOrderMutation.mutate({ id: product.id, displayOrder: otherProduct.displayOrder });
      updateOrderMutation.mutate({ id: otherProduct.id, displayOrder: product.displayOrder });
    }
  };

  if (isLoading) {
    return <div className="container mx-auto p-4">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavigation />

      <div className="container mx-auto p-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Restaurant Products</h1>
          <p className="text-gray-600">Manage your restaurant distribution products and categories</p>
        </div>

        <Tabs defaultValue="products" className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="products" className="flex items-center gap-2 font-semibold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <span className="text-lg font-bold">1.</span>
              <Package className="h-5 w-5" />
              Products
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2 font-semibold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <span className="text-lg font-bold">2.</span>
              <ListOrdered className="h-5 w-5" />
              Categories
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-4">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Package className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Products</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Price</p>
                <p className="text-2xl font-bold text-blue-600">{formatPrice(stats.averagePrice)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {isAdmin() ? (
              <Button 
                className="w-full" 
                onClick={() => {
                  setEditingProduct(null);
                  setIsAddModalOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            ) : (
              <div className="w-full text-center text-xs text-gray-400 py-3 border rounded">
                Admin Only - Add Product
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category: any) => {
                  const config = RESTAURANT_CATEGORY_CONFIG[category.name as keyof typeof RESTAURANT_CATEGORY_CONFIG];
                  const Icon = config?.icon || Package;
                  return (
                    <SelectItem key={category.id} value={category.name}>
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded ${config?.color || 'bg-gray-100 text-gray-800'}`}>
                          <Icon className="h-3 w-3" />
                        </div>
                        <span>{category.name} ({stats.categories[category.name] || 0})</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortField} onValueChange={(value) => setSortField(value as SortField)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual Order</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="pricePerUnit">Price</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </Button>

            {/* View Mode */}
            <div className="flex gap-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label>Show Inactive</Label>
            </div>
          </div>

          {/* CSV Import/Export */}
          {isAdmin() && (
            <div className="mt-4 pt-4 border-t">
              <RestaurantCSVImportExport 
                onImportComplete={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurant/products'] })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Products Display */}
      {viewMode === 'grid' ? (
        selectedCategory === 'all' ? (
          // Display products grouped by categories when showing all
          <div className="space-y-6">
            {categories.map((category) => {
              const categoryProducts = filteredProducts.filter(p => p.category === category.name);
              if (categoryProducts.length === 0) return null;
              
              const config = RESTAURANT_CATEGORY_CONFIG[category.name as keyof typeof RESTAURANT_CATEGORY_CONFIG];
              const Icon = config?.icon || Package;
              
              return (
                <div key={category.id} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${config?.color || 'bg-gray-100 text-gray-800'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <h2 className="text-lg font-semibold">{category.name}</h2>
                    <Badge variant="secondary" className="text-xs">{categoryProducts.length} products</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 lg:gap-4">
                    <AnimatePresence>
                      {categoryProducts.map((product) => (
                        <RestaurantProductCard
                          key={product.id}
                          product={product}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onToggleActive={handleToggleActive}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Display filtered products in grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 lg:gap-4">
            <AnimatePresence>
              {filteredProducts.map((product) => (
                <RestaurantProductCard
                  key={product.id}
                  product={product}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </AnimatePresence>
          </div>
        )
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedCategory === 'all' ? (
                    // Group by categories when showing all
                    categories.map((category) => {
                      const categoryProducts = filteredProducts.filter(p => p.category === category.name);
                      if (categoryProducts.length === 0) return null;
                      
                      const config = RESTAURANT_CATEGORY_CONFIG[category.name as keyof typeof RESTAURANT_CATEGORY_CONFIG];
                      const Icon = config?.icon || Package;
                      
                      return (
                        <React.Fragment key={category.id}>
                          <tr className="bg-gray-50">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className={`p-1 rounded ${config?.color || 'bg-gray-100 text-gray-800'}`}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <span className="font-semibold">{category.name}</span>
                                <Badge variant="secondary" className="ml-2">{categoryProducts.length}</Badge>
                              </div>
                            </td>
                          </tr>
                          {categoryProducts.map((product, index) => {
                            const categoryConfig = RESTAURANT_CATEGORY_CONFIG[product.category as keyof typeof RESTAURANT_CATEGORY_CONFIG];
                            return (
                              <tr key={product.id} className={!product.active ? 'opacity-50' : ''}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleMoveProduct(product, 'up')}
                                      disabled={sortField !== 'manual' || index === 0}
                                    >
                                      <ArrowUp className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleMoveProduct(product, 'down')}
                                      disabled={sortField !== 'manual' || index === categoryProducts.length - 1}
                                    >
                                      <ArrowDown className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div>
                                    <div className="font-medium">{product.name}</div>
                                    <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <Badge className={categoryConfig?.color || 'bg-gray-100 text-gray-800'}>
                                    {categoryConfig?.label || product.category}
                                  </Badge>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-green-600">
                                    {formatPrice(product.pricePerUnit)}/{product.unit}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {product.minOrderQuantity} {product.unit}
                                </td>
                                <td className="px-4 py-3">
                                  <Switch
                                    checked={product.active}
                                    onCheckedChange={(checked) => handleToggleActive(product.id, checked)}
                                  />
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEdit(product)}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleDelete(product.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    // Show filtered products for specific category
                    filteredProducts.map((product, index) => {
                      const categoryConfig = RESTAURANT_CATEGORY_CONFIG[product.category as keyof typeof RESTAURANT_CATEGORY_CONFIG];
                      return (
                        <tr key={product.id} className={!product.active ? 'opacity-50' : ''}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveProduct(product, 'up')}
                                disabled={sortField !== 'manual' || index === 0}
                              >
                                <ArrowUp className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveProduct(product, 'down')}
                                disabled={sortField !== 'manual' || index === filteredProducts.length - 1}
                              >
                                <ArrowDown className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium">{product.name}</div>
                              <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={categoryConfig?.color || 'bg-gray-100 text-gray-800'}>
                              {categoryConfig?.label || product.category}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-green-600">
                              {formatPrice(product.pricePerUnit)}/{product.unit}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {product.minOrderQuantity} {product.unit}
                          </td>
                          <td className="px-4 py-3">
                            <Switch
                              checked={product.active}
                              onCheckedChange={(checked) => handleToggleActive(product.id, checked)}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(product)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <RestaurantProductModal
        key={editingProduct?.id || 'new'}
        product={editingProduct}
        isOpen={isAddModalOpen || !!editingProduct}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
      />
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <RestaurantCategoriesManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}